// The page: the open screen until a session is open, then the editor, with the progress of an
// opening drawn over either while the Worker reads and aligns.

import { useEffect, useMemo, useRef, useState } from 'react'
import { stem } from '../core/files.ts'
import { Engine, type AutosaveNotice } from '../engine/client.ts'
import { desktop } from './desktop.ts'
import type { Autosaved, FileIn, Progress, State } from '../engine/protocol.ts'
import { Editor } from './Editor.tsx'
import { OpenScreen, type OpenRequest } from './OpenScreen.tsx'

interface Opening {
  title: string
  progress: Progress | null
  started: number
}

export function App() {
  // In the desktop app the autosave's files are the app's (brief 46).
  const engine = useMemo(() => new Engine(desktop ? desktop.store.bind(desktop) : null), [])
  const [session, setSession] = useState<{ state: State; key: number } | null>(null)
  const [opening, setOpening] = useState<Opening | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [autosaved, setAutosaved] = useState<Autosaved | null>(null)
  const [autosave, setAutosave] = useState<AutosaveNotice | null>(null)
  const [folder, setFolder] = useState<FileSystemDirectoryHandle | null>(null)
  const keys = useRef(0)

  useEffect(() => {
    engine.onProgress = progress => {
      // Each step, kept for the gates, which read what the sheet said even when it went by fast.
      ;((window as unknown as { alignProgress?: Progress[] }).alignProgress ??= []).push(progress)
      setOpening(o => (o ? { ...o, progress } : o))
    }
    engine.onAutosave = setAutosave
    void engine.request('autosaved', {}).then(setAutosaved, () => setAutosaved(null))
  }, [engine])

  const open = async (title: string, request: () => Promise<State>) => {
    setError(null)
    setOpening({ title, progress: null, started: performance.now() })
    try {
      const state = await request()
      setAutosave(null)
      setSession({ state, key: ++keys.current })
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setOpening(null)
    }
  }

  const align = (r: OpenRequest) =>
    open('Aligning', async () => {
      const read = async (f: File): Promise<FileIn> => ({ name: f.name, bytes: await f.arrayBuffer() })
      const source = await Promise.all(r.source.map(read))
      const target = await Promise.all(r.target.map(read))
      const name = stem(r.source[0]!.name)
      return engine.request('open', { source, target, sourceLanguage: r.sourceLanguage, targetLanguage: r.targetLanguage, name }, [...source, ...target].map(f => f.bytes))
    })

  const reopen = (file: File) =>
    open('Opening', async () => {
      if (/\.tmx$/i.test(file.name)) {
        const bytes = await file.arrayBuffer()
        return engine.request('tmx', { bytes, name: stem(file.name), file: file.name }, [bytes])
      }
      return engine.request('load', { json: await file.text(), name: stem(file.name), file: file.name })
    })

  const close = () => {
    setSession(null)
    void engine.request('autosaved', {}).then(setAutosaved, () => setAutosaved(null))
  }

  return (
    <div className="frame">
      {session ? (
        <Editor key={session.key} engine={engine} initial={session.state} folder={folder} autosave={autosave} onClose={close} />
      ) : (
        <OpenScreen
          autosaved={autosaved}
          error={error}
          folder={folder}
          onFolder={setFolder}
          onAlign={r => void align(r)}
          onReopen={f => void reopen(f)}
          onResume={() => void open('Resuming', () => engine.request('resume', {}))}
          onDiscard={() => void engine.request('discard', {}).then(() => setAutosaved(null))}
        />
      )}
      {opening && <ProgressSheet opening={opening} />}
    </div>
  )
}

/// What the Worker is doing while a session opens, with the seconds so far: aligning a book takes
/// a few seconds, and the page stays responsive meanwhile.
function ProgressSheet({ opening }: { opening: Opening }) {
  const [now, setNow] = useState(performance.now())
  useEffect(() => {
    const t = setInterval(() => setNow(performance.now()), 250)
    return () => clearInterval(t)
  }, [])
  const p = opening.progress
  const seconds = Math.max(0, (now - opening.started) / 1000)
  let line = 'Starting…'
  let share: number | null = null
  if (p?.stage === 'read') {
    line = `Reading ${p.name} (${p.index + 1} of ${p.count})`
    share = (p.index / p.count) * 0.1
  } else if (p?.stage === 'align') {
    const paragraphs = p.paragraphs[0] + p.paragraphs[1]
    line = `Cutting sentences and aligning ${paragraphs.toLocaleString()} paragraphs. A book takes a few seconds.`
  } else if (p?.stage === 'load') line = 'Reading the session'
  return (
    <div className="dialog" data-progress>
      <div className="dbox progress" role="status" aria-live="polite">
        <h2>{opening.title}</h2>
        <p data-progress-line>{line}</p>
        <span className={`busy-bar${share === null ? ' indeterminate' : ''}`}>
          <span style={share === null ? undefined : { width: `${Math.round(share * 100)}%` }} />
        </span>
        <p className="muted">{seconds.toFixed(0)} s</p>
      </div>
    </div>
  )
}
